import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Download, Trash2, ShieldAlert, Mail, User as UserIcon, KeyRound, AtSign } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Account() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Change-password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Change-email state
  const [newEmail, setNewEmail] = useState('');
  const [emailChangePassword, setEmailChangePassword] = useState('');
  const [emailChangeSubmitting, setEmailChangeSubmitting] = useState(false);
  const [emailChangeSent, setEmailChangeSent] = useState(false);

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    setEmailChangeSubmitting(true);
    try {
      await axios.post(
        `${API}/api/auth/change-email/request`,
        { new_email: newEmail.trim(), current_password: emailChangePassword },
        { withCredentials: true }
      );
      setEmailChangeSent(true);
      setEmailChangePassword('');
      toast.success('Check your new inbox to confirm the change.');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not request email change. Try again.');
    } finally {
      setEmailChangeSubmitting(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      toast.error('New passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters.');
      return;
    }
    if (currentPassword === newPassword) {
      toast.error('New password must be different from your current one.');
      return;
    }
    setChangingPassword(true);
    try {
      await axios.post(
        `${API}/api/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { withCredentials: true }
      );
      toast.success('Password updated. Use your new password next time you sign in.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err) {
      // Surface the backend's specific message (e.g. "That password is too common")
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not change password. Try again.');
    } finally {
      setChangingPassword(false);
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/api/account/export`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `unveiled-threads-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Your data export has been downloaded.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to export data. Try again later.');
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') {
      toast.error("You must type DELETE (in caps) to confirm.");
      return;
    }
    if (!password) {
      toast.error('Enter your password to confirm deletion.');
      return;
    }
    setDeleting(true);
    try {
      await axios.post(
        `${API}/api/account/delete`,
        { password, confirm: confirmText },
        { withCredentials: true }
      );
      toast.success('Your account has been permanently deleted.');
      // Force a full reload to the home page so the AuthContext re-checks and the user is logged out
      setTimeout(() => { window.location.href = '/'; }, 1200);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete account.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="account-page">
      <Header />
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-[#9CA3AF] hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <h1
          className="text-3xl md:text-4xl font-black tracking-tighter uppercase mb-2 text-white"
          style={{ fontFamily: 'Clash Display, sans-serif' }}
          data-testid="account-title"
        >
          ACCOUNT &amp; PRIVACY
        </h1>
        <p className="text-xs uppercase tracking-[0.2em] text-[#9CA3AF] mb-10">
          Manage your data — UK GDPR rights
        </p>

        {/* Account info */}
        <div className="border border-white/10 bg-[#0F0F0F] p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <UserIcon className="w-5 h-5 text-[#39FF14]" />
            <h2 className="text-lg font-bold text-white uppercase" style={{ fontFamily: 'Clash Display, sans-serif' }}>
              Your account
            </h2>
          </div>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between border-b border-white/5 pb-2">
              <dt className="text-[#9CA3AF]">Name</dt>
              <dd className="text-white" data-testid="account-name">{user.name}</dd>
            </div>
            <div className="flex justify-between border-b border-white/5 pb-2">
              <dt className="text-[#9CA3AF]">Email</dt>
              <dd className="text-white" data-testid="account-email">{user.email}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-[#9CA3AF]">Role</dt>
              <dd className="text-[#39FF14] uppercase tracking-wider text-xs" data-testid="account-role">{user.role}</dd>
            </div>
          </dl>
        </div>

        {/* Change email */}
        <div className="border border-white/10 bg-[#0F0F0F] p-6 mb-8" data-testid="change-email-section">
          <div className="flex items-center gap-3 mb-3">
            <AtSign className="w-5 h-5 text-[#39FF14]" />
            <h2 className="text-lg font-bold text-white uppercase" style={{ fontFamily: 'Clash Display, sans-serif' }}>
              Change email
            </h2>
          </div>
          <p className="text-sm text-[#C0C0C0] leading-relaxed mb-4">
            Update the email on your account. We&apos;ll send a link to your new address — nothing
            changes until you click it. Your old address gets a heads-up email for security.
          </p>
          {emailChangeSent ? (
            <div className="border border-[#39FF14]/30 bg-[#39FF14]/5 p-4 text-sm text-[#39FF14]" data-testid="change-email-sent">
              Check your new inbox to confirm the change. The link expires in 1 hour.
              <button type="button" className="ml-3 underline text-white/70 hover:text-white text-xs"
                onClick={() => { setEmailChangeSent(false); setNewEmail(''); }}>Send another</button>
            </div>
          ) : (
            <form onSubmit={handleChangeEmail} className="space-y-4">
              <div>
                <Label htmlFor="new-email" className="text-xs uppercase tracking-wider text-[#C0C0C0] mb-2 block">
                  New email address
                </Label>
                <Input id="new-email" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                  autoComplete="email" required
                  className="bg-black border-white/10 text-white rounded-none focus:border-[#39FF14]"
                  data-testid="new-email-input" />
              </div>
              <div>
                <Label htmlFor="email-change-password" className="text-xs uppercase tracking-wider text-[#C0C0C0] mb-2 block">
                  Current password
                </Label>
                <Input id="email-change-password" type="password" value={emailChangePassword}
                  onChange={(e) => setEmailChangePassword(e.target.value)}
                  autoComplete="current-password" required
                  className="bg-black border-white/10 text-white rounded-none focus:border-[#39FF14]"
                  data-testid="change-email-password-input" />
              </div>
              <Button type="submit" disabled={emailChangeSubmitting || !newEmail || !emailChangePassword}
                className="bg-[#39FF14] text-black hover:bg-[#39FF14]/90 rounded-none font-bold uppercase tracking-wider text-sm disabled:opacity-40"
                data-testid="change-email-submit">
                <AtSign className="w-4 h-4 mr-2" />
                {emailChangeSubmitting ? 'Sending link…' : 'Send verification link'}
              </Button>
            </form>
          )}
        </div>

        {/* Change password */}
        <div className="border border-white/10 bg-[#0F0F0F] p-6 mb-8" data-testid="change-password-section">
          <div className="flex items-center gap-3 mb-3">
            <KeyRound className="w-5 h-5 text-[#39FF14]" />
            <h2 className="text-lg font-bold text-white uppercase" style={{ fontFamily: 'Clash Display, sans-serif' }}>
              Change password
            </h2>
          </div>
          <p className="text-sm text-[#C0C0C0] leading-relaxed mb-4">
            Rotate your password without waiting for a reset email. Use at least 8 characters and
            avoid anything you re-use on other sites.
          </p>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label htmlFor="current-password" className="text-xs uppercase tracking-wider text-[#C0C0C0] mb-2 block">
                Current password
              </Label>
              <Input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                className="bg-black border-white/10 text-white rounded-none focus:border-[#39FF14]"
                data-testid="current-password-input"
                required
              />
            </div>
            <div>
              <Label htmlFor="new-password" className="text-xs uppercase tracking-wider text-[#C0C0C0] mb-2 block">
                New password
              </Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                className="bg-black border-white/10 text-white rounded-none focus:border-[#39FF14]"
                data-testid="new-password-input"
                required
              />
            </div>
            <div>
              <Label htmlFor="confirm-new-password" className="text-xs uppercase tracking-wider text-[#C0C0C0] mb-2 block">
                Confirm new password
              </Label>
              <Input
                id="confirm-new-password"
                type="password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                className="bg-black border-white/10 text-white rounded-none focus:border-[#39FF14]"
                data-testid="confirm-new-password-input"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={changingPassword || !currentPassword || !newPassword || !confirmNewPassword}
              className="bg-[#39FF14] text-black hover:bg-[#39FF14]/90 rounded-none font-bold uppercase tracking-wider text-sm disabled:opacity-40"
              data-testid="change-password-submit"
            >
              <KeyRound className="w-4 h-4 mr-2" />
              {changingPassword ? 'Updating…' : 'Update password'}
            </Button>
          </form>
        </div>

        {/* Export */}
        <div className="border border-white/10 bg-[#0F0F0F] p-6 mb-8">
          <div className="flex items-center gap-3 mb-3">
            <Download className="w-5 h-5 text-[#39FF14]" />
            <h2 className="text-lg font-bold text-white uppercase" style={{ fontFamily: 'Clash Display, sans-serif' }}>
              Export your data
            </h2>
          </div>
          <p className="text-sm text-[#C0C0C0] leading-relaxed mb-4">
            Download a JSON file containing every record we hold about you — orders, messages, listings,
            wishlist, and more. This satisfies your{' '}
            <span className="text-white">right to data portability (UK GDPR Article 20)</span>.
          </p>
          <p className="text-xs text-[#9CA3AF] mb-4">
            Note: payment / KYC data is held by Stripe, not us — request that copy from your Stripe dashboard.
          </p>
          <Button
            onClick={handleExport}
            disabled={exporting}
            className="bg-[#39FF14] text-black hover:bg-[#39FF14]/90 rounded-none font-bold uppercase tracking-wider text-sm"
            data-testid="export-data-button"
          >
            <Download className="w-4 h-4 mr-2" />
            {exporting ? 'Preparing…' : 'Download my data'}
          </Button>
        </div>

        {/* Other rights */}
        <div className="border border-white/10 bg-[#0F0F0F] p-6 mb-8">
          <div className="flex items-center gap-3 mb-3">
            <Mail className="w-5 h-5 text-[#39FF14]" />
            <h2 className="text-lg font-bold text-white uppercase" style={{ fontFamily: 'Clash Display, sans-serif' }}>
              Other GDPR requests
            </h2>
          </div>
          <p className="text-sm text-[#C0C0C0] leading-relaxed mb-3">
            For corrections, objections, restricted processing or any other privacy request, email us at{' '}
            <a href="mailto:privacy@unveiledthreads.co.uk" className="text-[#39FF14] hover:underline">
              privacy@unveiledthreads.co.uk
            </a>. We respond within 30 days.
          </p>
          <Link to="/privacy" className="text-xs text-[#39FF14] hover:underline uppercase tracking-wider" data-testid="account-privacy-link">
            Read our full Privacy Policy →
          </Link>
          <p className="text-[10px] text-[#6B7280] mt-4 pt-3 border-t border-white/5" data-testid="account-ico-reg">
            Unveiled Threads is registered with the UK ICO · Reg No. <span className="text-[#9CA3AF]">ZC176765</span>
          </p>
        </div>

        {/* Danger zone */}
        <div className="border border-red-500/40 bg-red-500/5 p-6 mb-8" data-testid="delete-zone">
          <div className="flex items-center gap-3 mb-3">
            <ShieldAlert className="w-5 h-5 text-red-400" />
            <h2 className="text-lg font-bold text-red-200 uppercase" style={{ fontFamily: 'Clash Display, sans-serif' }}>
              Delete account
            </h2>
          </div>
          <p className="text-sm text-red-100/80 leading-relaxed mb-2">
            Permanently delete your account and all personal data we hold about you. This is your{' '}
            <span className="text-white">right to erasure (UK GDPR Article 17)</span>.
          </p>
          <ul className="text-xs text-red-100/70 list-disc list-inside mb-4 space-y-1">
            <li>This cannot be undone.</li>
            <li>Your listings, messages, posts, wishlist and notifications will be wiped.</li>
            <li>Past orders are kept but anonymised (we&apos;re legally required to hold tax records for 6 years).</li>
            <li>If you sell on the platform, contact Stripe separately to close your connected account.</li>
          </ul>

          {!showDelete ? (
            <Button
              variant="outline"
              onClick={() => setShowDelete(true)}
              className="border-red-500/50 text-red-300 hover:bg-red-500/10 hover:text-red-200 rounded-none font-bold uppercase tracking-wider text-sm"
              data-testid="show-delete-button"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              I want to delete my account
            </Button>
          ) : (
            <div className="space-y-4 mt-4 border-t border-red-500/30 pt-4" data-testid="delete-confirm-form">
              <div>
                <Label htmlFor="del-password" className="text-xs uppercase tracking-wider text-red-200 mb-2 block">
                  Confirm your password
                </Label>
                <Input
                  id="del-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-black border-red-500/30 text-white rounded-none focus:border-red-500"
                  data-testid="delete-password-input"
                />
              </div>
              <div>
                <Label htmlFor="del-confirm" className="text-xs uppercase tracking-wider text-red-200 mb-2 block">
                  Type <span className="text-white font-mono">DELETE</span> to confirm
                </Label>
                <Input
                  id="del-confirm"
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="bg-black border-red-500/30 text-white rounded-none focus:border-red-500 font-mono"
                  data-testid="delete-confirm-input"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={handleDelete}
                  disabled={deleting || confirmText !== 'DELETE' || !password}
                  className="bg-red-500 text-white hover:bg-red-600 rounded-none font-bold uppercase tracking-wider text-sm disabled:opacity-40"
                  data-testid="confirm-delete-button"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {deleting ? 'Deleting…' : 'Permanently delete'}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => { setShowDelete(false); setPassword(''); setConfirmText(''); }}
                  className="text-[#C0C0C0] hover:text-white hover:bg-white/5 rounded-none uppercase tracking-wider text-sm"
                  data-testid="cancel-delete-button"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
